/**
 * Makes an ordinary web screen navigable by remote.
 *
 * ## Why this exists
 *
 * Home and the libraries are purpose-built for the TV: their cards call `useFocusable`
 * and their geometry is designed for a DPAD. The rest of the app is not, and never should
 * be — Sources, profile settings, the login form and the profile picker are screens you
 * touch a handful of times, mostly from a laptop. Writing and then maintaining a second
 * version of each of them would cost more than they are worth and would guarantee the two
 * drift apart.
 *
 * But "rarely used" is not "never used". Signing in has to happen at least once on the
 * television, and a profile has to be chosen every time. Without a focus system those
 * screens render and then ignore every key press, which on a TV is indistinguishable from
 * a crash.
 *
 * So instead of rewriting them, this scans its subtree for the elements that are already
 * interactive — buttons, links, inputs — and registers each one with the same focus
 * registry the TV screens use. Arrow keys then move between them geometrically, exactly
 * as they do between cards, and Enter clicks whatever is focused.
 *
 * ## What it deliberately does not touch
 *
 * Anything carrying `data-focusable`, or inside something carrying it. That attribute is
 * written by `useFocusable`, so an element that claimed its own id keeps it: the two
 * mechanisms coexist on the same screen without fighting over the same element.
 *
 * ## Why a MutationObserver rather than a re-scan per render
 *
 * The subtree is other components' output; this one does not re-render when they do. The
 * observer is the only way to notice that a list finished loading, a dialog opened, or a
 * button became disabled. `attributeFilter` is narrow on purpose — the scan writes an id
 * attribute of its own, and observing all attributes would make it retrigger itself
 * forever.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { useFocus } from './FocusContext';

/**
 * Transient UI that must take focus the moment it appears.
 *
 * A dialog is not just more elements on the same screen — it is a question the viewer has
 * to answer before anything else can happen. Registration alone does not move focus
 * (first-to-appear only wins when nothing is focused yet), so without this the PIN prompt
 * opened with focus still on the profile button behind it: the keypad was on screen, the
 * remote drove the wrong element, and the only way out was to kill the app. That is
 * exactly what a household member hit.
 */
const DIALOG = '[role="dialog"]';

const SELECTOR = [
  'button',
  'a[href]',
  'input:not([type="hidden"])',
  'select',
  'textarea',
  '[role="button"]',
].join(',');

/** Module-level so two mounted instances cannot hand out the same id. */
let counter = 0;

export function AutoFocusables({ children }: { children: ReactNode }) {
  const host = useRef<HTMLDivElement | null>(null);
  const { register, focus, focusedElement } = useFocus();

  useEffect(() => {
    const root = host.current;
    if (!root) return;

    const active = new Map<Element, () => void>();

    const scan = () => {
      const present = new Set<Element>();
      /** First focusable inside a dialog that focus is not already inside. */
      let claim: string | null = null;

      for (const node of root.querySelectorAll(SELECTOR)) {
        const el = node as HTMLElement;
        if (el.closest('[data-focusable]')) continue;
        if (el.hasAttribute('disabled')) continue;
        present.add(el);
        if (active.has(el)) continue;

        const id = el.getAttribute('data-auto-focus') ?? `auto:${counter++}`;
        el.setAttribute('data-auto-focus', id);

        // Claimed on first sight only, and only if focus is somewhere else entirely.
        // Re-claiming on a later scan would drag focus back to the first field every time
        // the dialog re-rendered — which it does on every keystroke.
        const dialog = el.closest(DIALOG);
        if (dialog && claim === null && !focusedElement()?.closest(DIALOG)) claim = id;
        active.set(
          el,
          register({
            id,
            group: 'auto',
            element: el,
            // `click()` rather than a synthetic event: it fires React's handler, submits a
            // form, follows a link, and on Tizen it is what opens the on-screen keyboard
            // for a text input. One call covers every element this selector matches.
            onSelect: () => el.click(),
          }),
        );
      }

      for (const [el, unregister] of active) {
        if (present.has(el)) continue;
        unregister();
        active.delete(el);
      }

      // After unregistering, so a dialog that just closed cannot leave focus on one of
      // its own dead elements.
      if (claim) focus(claim);
    };

    scan();
    const observer = new MutationObserver(scan);
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled'],
    });

    return () => {
      observer.disconnect();
      for (const unregister of active.values()) unregister();
    };
  }, [register, focus, focusedElement]);

  return (
    <div ref={host} className="tv__auto">
      {children}
    </div>
  );
}
